import { useState } from 'react'
import { Sun, Moon } from 'lucide-react'
import { useUIStore } from '../stores/uiStore'

export default function ThemeToggle() {
  const { theme, setTheme } = useUIStore()
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Resolve 'system' to actual theme
  const resolvedTheme = theme === 'system'
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme

  const toggleTheme = () => {
    if (isTransitioning) return

    setIsTransitioning(true)
    const newTheme = resolvedTheme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)

    // Reset transition state after animation
    setTimeout(() => setIsTransitioning(false), 600)
  }

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className="btn-glass group flex items-center justify-center hover:bg-[var(--luxury-glass-floating)] hover:scale-105 transform-gpu relative overflow-hidden cursor-pointer"
      style={{ padding: 'var(--bbs-spacing-md) var(--bbs-spacing-xl)', borderRadius: 'var(--bbs-radius-md)' }}
      aria-label={`Switch to ${resolvedTheme === 'light' ? 'dark' : 'light'} mode`}
      title={`Switch to ${resolvedTheme === 'light' ? 'dark' : 'light'} mode`}
    >
      {/* Glass texture overlay */}
      <div
        className="absolute inset-0 bg-[var(--glass-texture-light)] group-hover:bg-[var(--glass-texture-medium)] transition-all duration-300"
        style={{ borderRadius: 'var(--bbs-radius-md)' }}
      />

      {/* Shimmer effect on hover */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out"
        style={{ borderRadius: 'var(--bbs-radius-md)' }}
      />

      <div className="relative w-5 h-5 z-10">
        {/* Background glow */}
        <div className={`absolute inset-0 rounded-full blur-lg transition-all duration-500 ${
          resolvedTheme === 'light'
            ? 'bg-amber-400/40 scale-150 group-hover:bg-amber-400/60 group-hover:scale-[1.75]'
            : 'bg-indigo-400/40 scale-150 group-hover:bg-indigo-400/60 group-hover:scale-[1.75]'
        }`} />

        {/* Secondary glow ring */}
        <div className={`absolute inset-0 rounded-full blur-sm transition-all duration-500 ${
          resolvedTheme === 'light'
            ? 'bg-amber-300/30 scale-125'
            : 'bg-indigo-300/30 scale-125'
        }`} />

        {/* Sun icon */}
        <Sun
          className={`absolute inset-0 h-5 w-5 text-amber-500 transition-all duration-500 drop-shadow-lg group-hover:drop-shadow-xl ${
            resolvedTheme === 'light'
              ? 'opacity-100 rotate-0 scale-100 group-hover:scale-110 group-hover:rotate-12'
              : 'opacity-0 rotate-180 scale-75'
          }`}
          strokeWidth={1.5}
        />

        {/* Moon icon */}
        <Moon
          className={`absolute inset-0 h-5 w-5 text-indigo-400 transition-all duration-500 drop-shadow-lg group-hover:drop-shadow-xl ${
            resolvedTheme === 'dark'
              ? 'opacity-100 rotate-0 scale-100 group-hover:scale-110 group-hover:-rotate-12'
              : 'opacity-0 -rotate-180 scale-75'
          }`}
          strokeWidth={1.5}
        />
      </div>
    </button>
  )
}
